// Lib
import React, { useCallback, useState, useLayoutEffect, useRef } from 'react';
import 'cropperjs/dist/cropper.css';
import Cropper from 'cropperjs';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { inRange } from 'lodash/fp';

// Utils
import { getHeight, getWidth } from '../../../../../common/maths/geometry/rect';
import { getElementModalSize } from '../../../modal/elementModalUtils';
import {
    getCaption,
    getElementId,
    getHasDrawing,
    getShowCaption,
} from '../../../../../common/elements/utils/elementPropertyUtils';
import {
    getLargestImageSize,
    getImageEditorData,
    getImageEditorDataScaleXOrDefault,
    getImageEditorDataScaleYOrDefault,
    getOriginalImage,
    getImageEditorDataRotation,
    getOriginalImageWidth,
    getOriginalImageHeight,
} from '../imageModalUtils';
import { getImageSource } from '../../imageHelper';
import { appendToFilename, getFilenameFromUrl } from '../imageModalFileUtils';
import { fitSizeWithinBounds } from '../imageModalSizingUtils';

// Actions
import { updateElement } from '../../../actions/elementActions';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../../feature/elementFeatureSelector';
import { getElement } from '../../../selectors/elementSelector';

// Components
import Icon from '../../../../components/icons/Icon';
import Button from '../../../../components/buttons/Button';
import Spinner from '../../../../components/loaders/Spinner';
import ElementImage from '../../../../components/images/ElementImage';
import Caption from '../../../../components/caption/Caption';
import { SimpleDrawingSvg } from '../../../drawing/DrawingSvg';
import ImageModalToolbarSecondaryTools from '../toolbar/ImageModalToolbarSecondaryTools';
import ImageModalV2BackgroundToolbar from '../v2/toolbar/ImageModalV2BackgroundToolbar';
import ImageModalV2EditorToolbar from '../v2/toolbar/ImageModalV2EditorToolbar';
import type { ImMNElement, MNImageContent } from '../../../../../common/elements/elementModelTypes';

// Constants
import { IMAGE_MODAL_FOOTER_SIZE } from '../imageModalConstants';
import { IMAGE_TYPES } from '../../../../../common/media/mediaConstants';
import { ExperimentId } from '../../../../../common/experiments/experimentsConstants';

// Styles
import './ImageModalEditor.scss';

// Hooks
import { useImageRotation } from './hooks/useImageRotation';
import { useCropSaveHandler } from './hooks/useCropSaveHandler';
import { useImageEditorSaveFreeze } from './hooks/useImageEditorSaveFreeze';
import {
    IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY,
    useImageModalEditorImageLoader,
} from './hooks/useImageModalEditorImageLoader';
import { useBackgroundRemovalWipeOverlay } from './hooks/useBackgroundRemovalWipeOverlay';
import { useBackgroundRemovalCrop } from '../v2/content/hooks/useBackgroundRemovalCrop';
import { adjustCropperForRotation } from './utils/cropperUtils';
import { useTranslator } from '../../../../components/i18n/useTranslator';
import type { BackgroundRemovalProgress } from '../v2/toolbar/imageModalBackgroundRemovalProgressTypes';
import {
    buildStagedBackgroundRemovalChanges,
    isBackgroundRemovalActive,
} from '../v2/toolbar/imageModalBackgroundRemovalUtils';
import { ImageModalFlipDirection, trackImageModalFlip } from '../v2/toolbar/imageModalBackgroundRemovalAnalyticsUtils';
import type { GenericReduxDispatch } from '../../../../../common/actions/actionTypes';

/**
 * Resolve the full-source image used consistently for editor sizing, loading, and crop persistence.
 */
const getCropSourceImage = (element: ImMNElement, backgroundRemovalEditSource?: MNImageContent): MNImageContent =>
    backgroundRemovalEditSource || getOriginalImage(element);

export type ImageModalEditorProps = {
    element: ImMNElement;
    gridSize: number;
    attachment: unknown;
    windowWidth: number;
    windowHeight: number;
    idealHeight: number;
    // The available area the editor fits the source image into (V2). When absent (V1) the editor
    // derives its size from the window via getElementModalSize.
    editorBounds?: {
        width: number;
        height: number;
    };
    cancelEditing: () => void;
    // `didSave` is true only when a changed image was persisted (so a V2 caller can hold the frame until the
    // new source loads); a clean no-op close passes false to exit immediately.
    afterSaveCb: (didSave?: boolean) => void;
    toolbarVariant?: 'v1' | 'v2';
    toolbarMode?: 'edit' | 'background';
};

type ImageModalEditorContentProps = ImageModalEditorProps & {
    editorIsReady: boolean;
    isV2Toolbar: boolean;
    toolbarMode?: 'edit' | 'background';
    showImageReplacementWipe: boolean;
    showEditorLoadingPreview: boolean;
    toolbarClassName: string;
    backgroundRemovalProgress: BackgroundRemovalProgress | null;
    backgroundRemovalControlsDisabled?: boolean;
    // Whether the removed-background variant is staged to be applied on save. The `element` passed in already
    // reflects this variant (so the editor previews it), but it is only persisted when the user saves.
    backgroundRemovalStagedActive: boolean;
    backgroundRemovalStoredActive: boolean;
    onChangeBackgroundRemovalStagedActive: (active: boolean) => void;
    toast?: React.ReactNode;
    onEditorReadyChange: (ready: boolean) => void;
    setBackgroundRemovalProgress: (progress: BackgroundRemovalProgress | null) => void;
};

export const ImageModalEditorContent: React.FC<ImageModalEditorContentProps> = (props) => {
    const {
        element,
        gridSize,
        attachment,
        windowWidth,
        windowHeight,
        idealHeight,
        editorBounds,
        cancelEditing,
        afterSaveCb,
    } = props;

    const translate = useTranslator('popup', 'imageModal.editor');
    const useSecureMediaUrl = true;

    const renderSecureImagesThroughCF = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF),
    );

    const {
        backgroundRemovalEditSource,
        imageType,
        uploadEditedImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        updateElementImageData,
        revertToOriginalImageData,
        getRevertImage,
    } = useBackgroundRemovalCrop({
        element,
        elementId: getElementId(element),
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });

    // Background removal edits must load from the canonical source variant for the active side.
    // Otherwise repeated crops would keep re-cropping the already-edited visible image.
    const cropSourceImage = getCropSourceImage(element, backgroundRemovalEditSource);
    const {
        backgroundRemovalControlsDisabled,
        backgroundRemovalProgress,
        backgroundRemovalStagedActive,
        backgroundRemovalStoredActive,
        onChangeBackgroundRemovalStagedActive,
        editorIsReady,
        isV2Toolbar,
        toolbarMode,
        onEditorReadyChange,
        setBackgroundRemovalProgress,
        showEditorLoadingPreview,
        showImageReplacementWipe,
        toolbarClassName,
        toast,
    } = props;

    const dispatch = useDispatch<GenericReduxDispatch>();

    const [initialRotation] = useState(getImageEditorDataRotation(element) || 0);
    const [currentRotation, setCurrentRotation] = useState(initialRotation);
    const [initialScaleX] = useState(getImageEditorDataScaleXOrDefault(element));
    const [currentScaleX, setCurrentScaleX] = useState(initialScaleX);
    const [initialScaleY] = useState(getImageEditorDataScaleYOrDefault(element));
    const [currentScaleY, setCurrentScaleY] = useState(initialScaleY);
    const [hideDrawing, setHideDrawing] = useState(false);
    const [showWarningDialog, setShowWarningDialog] = useState(false);
    const [isCropToolSelected, setIsCropToolSelected] = useState(!isV2Toolbar);

    const imageRef = useRef<HTMLImageElement | null>(null);
    const cropperRef = useRef<Cropper | null>(null);
    const pendingTransformRef = useRef<(() => void) | null>(null);
    const isDirtyRef = useRef(false);
    const initialCropDataRef = useRef<Cropper.CropBoxData | null>(null);
    // Tracks the image currently loaded so a background-removal source swap (same element, different source
    // URL) can be told apart from a genuinely new image.
    const previousImageIdentityRef = useRef<{ elementId: string; imageType: string; url?: string } | null>(null);
    // Live rotate/flip/crop snapshotted before a background-removal source swap, re-applied once the swapped-in
    // image loads so the user's in-progress edits survive the reload.
    const pendingCropperDataRef = useRef<Cropper.Data | null>(null);

    const elementId = getElementId(element);
    const caption = getShowCaption(element) && getCaption(element);
    const shouldSaveStagedBackgroundRemovalRender =
        isV2Toolbar &&
        toolbarMode === 'background' &&
        backgroundRemovalStoredActive !== backgroundRemovalStagedActive &&
        !!backgroundRemovalEditSource &&
        !!getImageEditorData(element);

    const originalImageWidth = getWidth(cropSourceImage) || getOriginalImageWidth({ element, gridSize, attachment });
    const originalImageHeight = getHeight(cropSourceImage) || getOriginalImageHeight({ element, gridSize, attachment });

    const isRotatedOnSide = !!currentRotation && currentRotation !== -180;

    // Flip the image width and height if it's rotated onto its side
    const imageWidth = isRotatedOnSide ? originalImageHeight : originalImageWidth;
    const imageHeight = isRotatedOnSide ? originalImageWidth : originalImageHeight;

    let modalWidth: number;
    let modalHeight: number;
    if (editorBounds) {
        const fittedEditorImage = fitSizeWithinBounds({ width: imageWidth, height: imageHeight }, editorBounds);
        modalWidth = fittedEditorImage.width;
        modalHeight = fittedEditorImage.height;
    } else {
        const calculatedModalSize = getElementModalSize({
            windowWidth,
            windowHeight,
            imageWidth,
            imageHeight,
            idealHeight,
            caption,
            footerSize: IMAGE_MODAL_FOOTER_SIZE,
            modalMinWidth: 0,
            modalMinHeight: 0,
            gridSize,
            modalVerticalMarginGU: 5,
        });
        modalWidth = calculatedModalSize.modalWidth;
        modalHeight = calculatedModalSize.modalHeight;
    }

    const svgWrapperStyle = {
        width: '100%',
        height: '100%',
        // Mirror the image flip on the drawing overlay. Use the delta from the saved scale so the overlay
        // sits in its stored orientation on open (delta 1) and only flips with new edits. Rotation is excluded
        // because the rotated drawing is hidden, not transformed.
        transform: `scaleX(${currentScaleX / initialScaleX}) scaleY(${currentScaleY / initialScaleY})`,
        transformOrigin: 'center',
    };

    const loadingImageStyles = {
        width: isRotatedOnSide ? modalHeight : modalWidth,
        height: isRotatedOnSide ? modalWidth : modalHeight,
        transform: isV2Toolbar
            ? `rotate(${initialRotation}deg) scaleX(${initialScaleX}) scaleY(${initialScaleY})`
            : `rotate(${initialRotation}deg)`,
    };

    const imageSize = getLargestImageSize(cropSourceImage);
    const largestUneditedImageUrl = getImageSource({
        imageDetails: cropSourceImage,
        imageSize,
        elementId,
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });

    const editorImageLoadKey = `${elementId}-${imageType}-${initialRotation}-${initialScaleX}-${initialScaleY}-${largestUneditedImageUrl || ''}`;

    // Hold the editor's rendered view stable across a save so the in-flight Redux updates don't make the
    // image flash or reload mid-save. See the hook for the full rationale.
    const { isFrozen, onSaveStart, displayElement, activeEditorImageLoadKey, activeLargestUneditedImageUrl } =
        useImageEditorSaveFreeze({
            enabled: isV2Toolbar,
            element,
            editorImageLoadKey,
            largestUneditedImageUrl,
        });

    // Drawing presence/visibility follows the displayed (possibly frozen) element, not the live prop, so
    // the overlay stays put while a save is in flight.
    const hasDrawing = getHasDrawing(displayElement);

    const { handleRotateImage, handleFlipImageHorizontally, handleFlipImageVertically } = useImageRotation({
        cropperRef,
        isDirtyRef,
        initialCropDataRef,
        currentRotation,
        initialRotation,
        currentScaleX,
        currentScaleY,
        setCurrentRotation,
        setCurrentScaleX,
        setCurrentScaleY,
        setHideDrawing,
    });

    // Apply the staged background-removal variant once crop/rotate persistence has run, reading the element
    // fresh so it reflects any cropped variants produced by the save above. The util decides whether a full
    // toggle or just a variant/background-flag update is needed (see buildStagedBackgroundRemovalChanges).
    const commitStagedBackgroundRemoval = useCallback(() => {
        dispatch((dispatchThunk, getState) => {
            const freshElement = getElement(getState(), { elementId });
            if (!freshElement) return;

            const changes = buildStagedBackgroundRemovalChanges({
                element: freshElement,
                active: backgroundRemovalStagedActive,
            });
            if (!changes) return;

            dispatchThunk(updateElement({ id: elementId, changes, silent: true, sync: true }));
        });
    }, [dispatch, elementId, backgroundRemovalStagedActive]);

    const handleAfterSave = useCallback(
        (didSave?: boolean) => {
            commitStagedBackgroundRemoval();
            afterSaveCb(didSave);
        },
        [commitStagedBackgroundRemoval, afterSaveCb],
    );

    const { handleSave } = useCropSaveHandler({
        cropperRef,
        isDirtyRef,
        elementId,
        cropSourceImage,
        imageType,
        largestUneditedImageUrl,
        hideDrawing,
        getFilenameFromUrl,
        appendToFilename,
        inRange,
        updateElementImageData,
        revertToOriginalImageData,
        uploadEditedImage,
        onSaveStart,
        shouldSaveWhenClean: shouldSaveStagedBackgroundRemovalRender,
        getRevertedImage: getRevertImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        afterSaveCb: handleAfterSave,
    });

    useLayoutEffect(() => {
        // While frozen the editor is intentionally holding a snapshot for an in-flight save, so don't
        // reset its rotation/scale state back to the (now-changing) element prop.
        if (isFrozen) return;

        const previousIdentity = previousImageIdentityRef.current;
        // A background-removal toggle swaps the underlying source variant while the element/imageType stay
        // the same. That's not a fresh image, so the user's in-progress rotate/flip/crop must be preserved
        // and re-applied to the swapped-in source — otherwise the editor snaps back to the stored scale and
        // the (un-flipped) original briefly flashes during the transition.
        const isBackgroundRemovalVariantSwap =
            !!previousIdentity &&
            previousIdentity.elementId === elementId &&
            previousIdentity.imageType === imageType &&
            previousIdentity.url !== largestUneditedImageUrl;

        previousImageIdentityRef.current = { elementId, imageType, url: largestUneditedImageUrl };

        if (isBackgroundRemovalVariantSwap) {
            // Snapshot the live cropper state before the reload tears the cropper down. It's re-applied in
            // loadCanvasIntoCropper's ready callback once the swapped-in image is loaded.
            pendingCropperDataRef.current = cropperRef.current?.getData() ?? null;
            onEditorReadyChange(false);
            return;
        }

        pendingCropperDataRef.current = null;
        setCurrentRotation(initialRotation);
        setCurrentScaleX(initialScaleX);
        setCurrentScaleY(initialScaleY);
        setHideDrawing(false);
        setShowWarningDialog(false);
        pendingTransformRef.current = null;
        onEditorReadyChange(false);
        isDirtyRef.current = false;
        initialCropDataRef.current = null;
    }, [
        elementId,
        imageType,
        initialRotation,
        initialScaleX,
        initialScaleY,
        isFrozen,
        largestUneditedImageUrl,
        onEditorReadyChange,
    ]);

    // The transform the cropper currently displays the image with, so the wipe frame and any re-applied
    // edits stay in the orientation the user set rather than reverting to the un-flipped source.
    const editorImageTransform = `rotate(${currentRotation}deg) scaleX(${currentScaleX}) scaleY(${currentScaleY})`;

    const { handleImageDataUrlLoad, handleWipeAnimationEnd, wipeOverlay } = useBackgroundRemovalWipeOverlay({
        enabled: showImageReplacementWipe,
        editorImageLoadKey: activeEditorImageLoadKey,
        editorIsReady,
        imageTransform: editorImageTransform,
    });

    useImageModalEditorImageLoader({
        imageRef,
        cropperRef,
        isDirtyRef,
        element,
        imageType,
        loadKey: activeEditorImageLoadKey,
        loadStrategy: isV2Toolbar
            ? IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY.KEEP_PREVIOUS_IMAGE_UNTIL_READY
            : IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY.CLEAR_IMAGE_BEFORE_LOAD,
        sourceUrl: activeLargestUneditedImageUrl,
        onEditorReadyChange,
        onImageDataUrlLoad: handleImageDataUrlLoad,
        pendingCropperDataRef,
    });

    useLayoutEffect(() => {
        const cropper = cropperRef.current;
        if (!editorIsReady || !cropper) return;
        if (!initialCropDataRef.current) return;

        adjustCropperForRotation(cropper, modalWidth, initialCropDataRef.current);
    }, [currentRotation, editorIsReady, modalWidth]);

    // Rotation clears an existing drawing (the overlay can't follow a quarter-turn), so warn first.
    // Flipping keeps and transforms the drawing, so it applies immediately without a warning.
    const handleRotateClick = () => {
        if (!hideDrawing && hasDrawing) {
            pendingTransformRef.current = handleRotateImage;
            setShowWarningDialog(true);
            return;
        }

        handleRotateImage();
    };

    const handleFlipHorizontalClick = () => {
        trackImageModalFlip(ImageModalFlipDirection.HORIZONTAL);
        handleFlipImageHorizontally();
    };

    const handleFlipVerticalClick = () => {
        trackImageModalFlip(ImageModalFlipDirection.VERTICAL);
        handleFlipImageVertically();
    };

    const handleConfirmRotation = () => {
        setShowWarningDialog(false);
        pendingTransformRef.current?.();
        pendingTransformRef.current = null;
    };

    const handleCancelRotation = () => {
        setShowWarningDialog(false);
        pendingTransformRef.current = null;
    };

    const toggleCropTool = () => {
        setIsCropToolSelected((isSelected) => !isSelected);
    };

    const toolbarDisabled = isFrozen || !editorIsReady || showWarningDialog || !!backgroundRemovalControlsDisabled;
    const isBackgroundRemovalTransparentPreview =
        isV2Toolbar && toolbarMode === 'background' && backgroundRemovalStagedActive;
        
    return (
        <>
            <div className="image-modal-content">
                <div className="image-container">
                    <div
                        className={classNames('ImageEditor', {
                            'ImageEditor-v2': isV2Toolbar,
                        })}
                    >
                        <div
                            className={classNames('editor', {
                                'editor-v2': isV2Toolbar,
                                loading: showEditorLoadingPreview,
                                'crop-tool-disabled': !isCropToolSelected,
                                'background-removal-transparent-preview': isBackgroundRemovalTransparentPreview,
                            })}
                            style={{ width: modalWidth, height: modalHeight }}
                        >
                            {showEditorLoadingPreview && (
                                <div className="loading-message">
                                    <div className="loading-image" style={loadingImageStyles}>
                                        <ElementImage
                                            useSecureMediaUrl={useSecureMediaUrl}
                                            showBrokenIconOnError
                                            element={displayElement}
                                            imageData={cropSourceImage}
                                            imageType={IMAGE_TYPES.ELEMENT}
                                            widthPx={modalWidth}
                                            key={`${elementId}-${largestUneditedImageUrl}`}
                                        />
                                    </div>
                                    <SimpleDrawingSvg element={displayElement} />
                                    {!isV2Toolbar && (
                                        <div className="loading-overlay">
                                            <Spinner show />
                                            {translate('loadingEditor')}…
                                        </div>
                                    )}
                                </div>
                            )}

                            <img
                                ref={(c) => {
                                    imageRef.current = c;
                                }}
                                className="editor-source-image"
                                style={{ width: modalWidth, height: modalHeight }}
                            />
                            {wipeOverlay && (
                                // The clip-path wipe lives on the outer element (screen space) so it always
                                // sweeps right-to-left, while the flip/rotate transform is applied to the inner
                                // image layer only — otherwise a horizontal flip would also mirror the wipe.
                                <div
                                    key={wipeOverlay.renderKey}
                                    className={classNames('bg-removal-wipe-overlay', {
                                        'is-wiping': wipeOverlay.wiping,
                                    })}
                                    onAnimationEnd={handleWipeAnimationEnd}
                                    aria-hidden
                                >
                                    <div
                                        className="bg-removal-wipe-overlay-image"
                                        style={{
                                            // Size to the un-rotated footprint (swapping w/h when rotated onto
                                            // its side) before the rotate transform is applied, exactly like
                                            // loadingImageStyles. Otherwise the outgoing frame is fitted into the
                                            // rotated footprint and then rotated again, leaving the stale image at
                                            // a different scale/offset to the cropper underneath — two mismatched
                                            // images during the wipe.
                                            width: isRotatedOnSide ? modalHeight : modalWidth,
                                            height: isRotatedOnSide ? modalWidth : modalHeight,
                                            backgroundImage: `url(${wipeOverlay.dataUrl})`,
                                            transform: wipeOverlay.transform,
                                        }}
                                    />
                                </div>
                            )}
                            {hasDrawing && !hideDrawing && (editorIsReady || showImageReplacementWipe) && (
                                <div className="drawing-svg-container">
                                    <div className="drawing-wrapper" style={svgWrapperStyle}>
                                        <SimpleDrawingSvg element={displayElement} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <Caption
                        element={element}
                        textContent={getCaption(element)}
                        captionVisible={getShowCaption(element)}
                    />
                    {showWarningDialog && (
                        <div className="warning-dialog">
                            <div className="dialog-body">
                                <div className="dialog-text">
                                    <span className="warning-label">{translate('warningLabel')}</span>
                                    &nbsp;
                                    {translate('warningMessage')}
                                </div>

                                <div className="buttons">
                                    <Button className="StyledButton secondary" onClickFn={handleCancelRotation}>
                                        {translate('cancel')}
                                    </Button>
                                    <Button className="StyledButton danger" onClickFn={handleConfirmRotation}>
                                        {translate('continueAnyway')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {toast}
            <ImageModalToolbarSecondaryTools className={toolbarClassName}>
                {isV2Toolbar ? (
                    toolbarMode === 'background' ? (
                        <ImageModalV2BackgroundToolbar
                            element={element}
                            cropSourceImage={cropSourceImage}
                            largestImageUrl={largestUneditedImageUrl}
                            imageType={imageType}
                            progress={backgroundRemovalProgress}
                            setProgress={setBackgroundRemovalProgress}
                            disabled={toolbarDisabled}
                            stagedActive={backgroundRemovalStagedActive}
                            onChangeStagedActive={onChangeBackgroundRemovalStagedActive}
                            onCancelClick={cancelEditing}
                            onSaveClick={handleSave}
                        />
                    ) : (
                        <ImageModalV2EditorToolbar
                            cropActive={isCropToolSelected}
                            disabled={toolbarDisabled}
                            onCropClick={toggleCropTool}
                            onRotateClick={handleRotateClick}
                            onFlipHorizontalClick={handleFlipHorizontalClick}
                            onFlipVerticalClick={handleFlipVerticalClick}
                            onCancelClick={cancelEditing}
                            onSaveClick={handleSave}
                        />
                    )
                ) : (
                    <>
                        <Button
                            className="StyledButton secondary image-control rotate"
                            onClickFn={handleRotateClick}
                            disabled={toolbarDisabled}
                        >
                            <Icon name="modal-toolbar-rotate" />
                            {translate('rotateLeft')}
                        </Button>

                        <Button className="StyledButton secondary cancel no-hover" onClickFn={cancelEditing}>
                            {translate('cancel')}
                        </Button>

                        <Button className="StyledButton primary save" onClickFn={handleSave} disabled={toolbarDisabled}>
                            {translate('save')}
                        </Button>
                    </>
                )}
            </ImageModalToolbarSecondaryTools>
        </>
    );
};

const ImageModalEditor: React.FC<ImageModalEditorProps> = (props) => {
    const [editorIsReady, setEditorIsReady] = useState(false);
    const isV2Toolbar = props.toolbarVariant === 'v2';

    const toolbarClassName = classNames('ImageEditorModalToolbar', {
        'ImageEditorModalToolbar-v2': isV2Toolbar,
    });

    return (
        <ImageModalEditorContent
            {...props}
            editorIsReady={editorIsReady}
            isV2Toolbar={isV2Toolbar}
            showImageReplacementWipe={false}
            showEditorLoadingPreview={!editorIsReady}
            toolbarClassName={toolbarClassName}
            backgroundRemovalProgress={null}
            backgroundRemovalStagedActive={isBackgroundRemovalActive(props.element)}
            backgroundRemovalStoredActive={isBackgroundRemovalActive(props.element)}
            onChangeBackgroundRemovalStagedActive={() => undefined}
            onEditorReadyChange={setEditorIsReady}
            setBackgroundRemovalProgress={() => undefined}
        />
    );
};

export default ImageModalEditor;
